<script lang="ts">
import type { Colors, Sizes, SlotInterface } from 'types';
import type { UseIconsProps } from 'composables/useIcons';

export interface HeadingProps extends UseIconsProps {
  /**
   * What this component should be rendered as
   * If you dont want to pollute you heading. Use `span`
   * @defaultValue 'h2'
   */
  as?: ('h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span') | null;
  title?: string;
  color?: Colors;
  size?: Sizes | '2xl';
  class?: string;
}

export interface HeadingSlots extends SlotInterface<{
  leading?(props?: {}): unknown;
  default?(props?: {}): unknown;
  trailing?(props?: {}): unknown;
}> {}
</script>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useIcons } from 'composables/useIcons';
import { useConditionalSlots } from 'composables/useConditionalSlots';

const props = withDefaults(defineProps<HeadingProps>(), {
  as: 'h2'
});

const { isLeading, isTrailing, leadingIconName, trailingIconName } = useIcons(
  computed(() => ({ ...props, loading: false }))
);

const classes = computed(() => [
  'e-heading',
  props.color && `e-heading-color-${props.color}`,
  props.size && `e-heading-size-${props.size}`,
  props.class
]);

const elRef = ref<HTMLElement | null>(null);

const {
  leading: hasLeading,
  default: hasDefault,
  trailing: hasTrailing
} = useConditionalSlots<HeadingSlots>(elRef, ['leading', 'default', 'trailing']);
</script>

<template>
  <component
    :is="as"
    ref="elRef"
    :class="classes"
  >
    <div v-if="hasLeading || (isLeading && leadingIconName)">
      <slot name="leading">
        <e-icon
          v-if="isLeading && leadingIconName"
          :name="leadingIconName"
        />
      </slot>
    </div>
    <div v-if="hasDefault || title">
      <slot>
        {{ title }}
      </slot>
    </div>
    <div v-if="hasTrailing || (isTrailing && trailingIconName)">
      <slot name="trailing">
        <e-icon
          v-if="isTrailing && trailingIconName"
          :name="trailingIconName"
        />
      </slot>
    </div>
  </component>
</template>
